/** design.md §P1 — Sign in. One action, domain stated before the attempt. */

import { useMutation } from '@tanstack/react-query'
import { AlertCircle, TerminalSquare } from 'lucide-react'
import * as React from 'react'
import { useSearchParams } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/form'
import { api, ApiError } from '@/lib/api'
import { useAuthConfig, usePageTitle } from '@/lib/title'
import { useAuth } from '@/providers/auth'

/**
 * The `?error=` reasons the backend redirects with, in the user's language.
 * Anything unmapped falls through to a generic line rather than showing a code.
 */
const REASONS: Record<string, string> = {
  // The approved domains are said on the page itself (`allowed_domains`).
  domain_not_allowed: "That account isn't on an approved domain. Sign in with one that is.",
  email_unverified: 'That Google account has an unverified email address.',
  user_disabled: 'This account has been deactivated. Contact an administrator.',
  invalid_state: 'That sign-in link expired. Try again.',
  state_expired: 'That sign-in link expired. Try again.',
  missing_code: 'Google did not complete the sign-in. Try again.',
  token_exchange_failed: 'Google rejected the sign-in. Try again.',
  no_access_token: 'Google rejected the sign-in. Try again.',
  userinfo_failed: 'Could not read your Google profile. Try again.',
  no_email: 'That Google account has no email address.',
  google_unreachable: 'Could not reach Google. Check your connection and try again.',
  google_access_denied: 'Sign-in was cancelled.',
}

export function LoginPage() {
  const [params] = useSearchParams()
  const { refresh } = useAuth()
  const [devEmail, setDevEmail] = React.useState('')
  const [error, setError] = React.useState<string | null>(() => {
    const reason = params.get('error')
    if (!reason) return null
    return REASONS[reason] ?? 'Sign-in failed. Try again.'
  })

  const config = useAuthConfig()
  usePageTitle(['Sign in'])

  const google = useMutation({
    mutationFn: () => api.post<{ authorize_url: string }>('/api/auth/google/start'),
    onSuccess: ({ authorize_url }) => {
      window.location.href = authorize_url
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Sign-in failed.'),
  })

  const devLogin = useMutation({
    mutationFn: () => api.post('/api/auth/dev-login', { email: devEmail }),
    onSuccess: () => void refresh(),
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Sign-in failed.'),
  })

  const domains = config.data?.allowed_domains ?? []

  return (
    <div className="flex min-h-full items-center justify-center bg-bg px-4 py-12">
      <div className="w-full max-w-sm">
        <h1 className="mb-6 text-center text-xl font-semibold tracking-tight">
          {/* The deployment's own name (settings.app_name), never one written here. */}
          {config.data?.product_name ?? 'Sign in'}
        </h1>

        <div className="rounded-lg border border-border bg-surface p-5">
          {error ? (
            <div
              role="alert"
              className="mb-4 flex gap-2 rounded-md bg-danger-soft px-3 py-2 text-xs text-danger"
            >
              <AlertCircle className="mt-px size-3.5 shrink-0" />
              <span>{error}</span>
            </div>
          ) : null}

          {config.data?.google_enabled ? (
            <Button
              variant="primary"
              className="h-10 w-full"
              loading={google.isPending}
              onClick={() => google.mutate()}
            >
              <GoogleMark />
              Continue with Google
            </Button>
          ) : (
            <p className="rounded-md bg-surface-2 px-3 py-2 text-xs text-muted">
              Google sign-in is not configured on this server. Set{' '}
              <code className="font-mono">GOOGLE_CLIENT_ID</code> and{' '}
              <code className="font-mono">GOOGLE_CLIENT_SECRET</code> to enable it.
            </p>
          )}

          {/* design.md §P1: the allowed domain is stated before the attempt. */}
          {domains.length > 0 ? (
            <p className="mt-3 text-center text-[11px] text-faint">
              Only {domains.map((d) => `@${d}`).join(' or ')} accounts can sign in.
            </p>
          ) : null}

          {config.data?.dev_login_enabled ? (
            <div className="mt-5 border-t border-dashed border-border pt-4">
              <p className="mb-2 flex items-center gap-1.5 text-[11px] font-medium text-warn">
                <TerminalSquare className="size-3.5" />
                Development sign-in
              </p>
              <form
                className="flex gap-2"
                onSubmit={(event) => {
                  event.preventDefault()
                  setError(null)
                  devLogin.mutate()
                }}
              >
                <Input
                  type="email"
                  required
                  value={devEmail}
                  onChange={(event) => setDevEmail(event.target.value)}
                  placeholder={domains[0] ? `you@${domains[0]}` : 'you@example.com'}
                  aria-label="Email address for development sign-in"
                  className="h-8 text-[13px]"
                />
                <Button type="submit" size="sm" variant="secondary" loading={devLogin.isPending}>
                  Sign in
                </Button>
              </form>
              <p className="mt-1.5 text-[11px] text-faint">
                Dev only. Still restricted to the allowed domains.
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

function GoogleMark() {
  return (
    <svg viewBox="0 0 18 18" className="size-4" aria-hidden>
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.91c1.7-1.57 2.69-3.88 2.69-6.62Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.81 5.96-2.18l-2.91-2.26c-.81.54-1.84.86-3.05.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.34A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72a5.41 5.41 0 0 1 0-3.44V4.94H.96a9 9 0 0 0 0 8.12l3.01-2.34Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.94l3.01 2.34C4.68 5.16 6.66 3.58 9 3.58Z"
      />
    </svg>
  )
}
